import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';

import {
  ButtonModule,
  DIALOG_DATA,
  DialogRef,
  IconModule,
  RadioOptionModule,
  SpinnerDirective,
  TextAreaComponentModule,
} from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';
import { MessageDatePipe } from '@fb/pipes/date.pipe';
import {
  AiChatContext,
  BotChatMessageView,
  ChatMessageFeedback,
  ChatMessageReplyTypes,
  ChatMessageRole,
  ChatMessage,
  InternalMessageStatus,
  ModelFormGroup,
} from '@fb/models';
import { DictionariesService } from '@fb/services/dictionaries.service';
import { isAuthSessionError } from '@fb/services/auth/auth-session.error';
import { SlackLinkDirective } from '@fb/directives/slack-link.directive';
import { TranslateInPipe } from '@fb/pipes/translate-in.pipe';

import {
  AI_CHAT_DEFAULT_GREETING_LANG,
  AI_CHAT_QUESTION_TEXTAREA_MAX_HEIGHT_PX,
  AI_CHAT_QUESTION_TEXTAREA_MAX_HEIGHT_VAR,
  AI_CHAT_QUESTION_TEXTAREA_MIN_HEIGHT_PX,
  AI_CHAT_QUESTION_TEXTAREA_MIN_HEIGHT_VAR,
  AI_CHAT_SCROLL_BOTTOM_THRESHOLD_PX,
  FEEDBACK_COMMENT_MAX_LENGTH,
} from './ai-chat.constants';
import { AiChatFormDialogData, AiChatInitialAction } from './ai-chat.types';
import { AiChatStore } from './store';
import { UserChatMessageComponent } from './components/user-chat-message/user-chat-message.component';
import { BotChatMessageComponent } from './components/bot-chat-message/bot-chat-message.component';
import { AiChatHistoryPanelComponent } from './components/ai-chat-history-panel/ai-chat-history-panel.component';
import { AiChatTabBridgeService } from './ai-chat-tab-bridge.service';
import { AiChatWorkContextPickerDialog } from './ai-chat-work-context-picker.dialog';
import { ChatTextareaAutosizeDirective } from './directives/chat-textarea-autosize.directive';
import { formatAiChatThreadListTitle } from './helpers/format-ai-chat-thread-list-title';
import { ctaToInitialAction } from './helpers/cta-to-initial-action';
import { workChatBadge } from './helpers/work-context';
import {
  CtaCooldownService,
  CtaDefinition,
  CtaListComponent,
  CtaStore,
  CtaSurface,
  PageContextService,
} from '../cta';

@Component({
  selector: 'pu-ai-chat-form',
  imports: [
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatButton,
    MatDialogClose,
    OverlayModule,
    ButtonModule,
    IconModule,
    TranslateModule,
    ReactiveFormsModule,
    MessageDatePipe,
    SpinnerDirective,
    DatePipe,
    NgClass,
    RadioOptionModule,
    TextAreaComponentModule,
    SlackLinkDirective,
    UserChatMessageComponent,
    BotChatMessageComponent,
    AiChatHistoryPanelComponent,
    ChatTextareaAutosizeDirective,
    TranslateInPipe,
    CtaListComponent,
  ],
  providers: [
    AiChatStore,
    AiChatWorkContextPickerDialog,
    TranslateInPipe,
    DatePipe,
    CtaStore,
  ],
  templateUrl: './ai-chat-form.component.html',
  styleUrl: './ai-chat-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatFormComponent {
  /** Expose enum for template tab checks. */
  readonly AiChatContext = AiChatContext;
  readonly CtaSurface = CtaSurface;

  readonly isFullPage = input(false);

  /** Question textarea min/max height vars for SCSS. */
  @HostBinding(`style.${AI_CHAT_QUESTION_TEXTAREA_MIN_HEIGHT_VAR}`)
  protected readonly chatQuestionTextareaMinHeightCss =
    `${AI_CHAT_QUESTION_TEXTAREA_MIN_HEIGHT_PX}px`;

  @HostBinding(`style.${AI_CHAT_QUESTION_TEXTAREA_MAX_HEIGHT_VAR}`)
  protected readonly chatQuestionTextareaMaxHeightCss =
    `${AI_CHAT_QUESTION_TEXTAREA_MAX_HEIGHT_PX}px`;

  private readonly messagesBody = viewChild<ElementRef<HTMLElement>>('messagesBody');
  private readonly questionAutosize = viewChild(ChatTextareaAutosizeDirective);

  readonly #dialogRef = inject(DialogRef, { optional: true });
  readonly #dialogData = inject<AiChatFormDialogData>(DIALOG_DATA, { optional: true });
  readonly #formBuilder = inject(FormBuilder);
  readonly #aiChatStore = inject(AiChatStore);
  readonly #datePipe = inject(DatePipe);
  readonly #dictionariesService = inject(DictionariesService);
  /** Tab bridge (full-page only). */
  readonly tabNav = inject(AiChatTabBridgeService, { optional: true });
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);
  readonly #ctaStore = inject(CtaStore);
  readonly #ctaCooldown = inject(CtaCooldownService);
  readonly #pageContext = inject(PageContextService);

  /** Header context tabs from `tabNav.allowedContexts()`. Only shown when more than one context is available. */
  readonly chatHeaderContexts = computed((): AiChatContext[] => {
    const nav = this.tabNav;

    if (nav == null) {
      return [];
    }

    const contexts = nav.allowedContexts();

    return contexts.length > 1 ? contexts : [];
  });

  readonly showHistoryPanel = computed(() => this.isFullPage());
  /** Popup only: toggles the full-panel history overlay. */
  readonly #popupHistoryOpen = signal(false);
  readonly popupHistoryOpen = this.#popupHistoryOpen.asReadonly();

  /**
   * Show the bot name header:
   * - always in popup mode,
   * - in full-page when no context tabs are rendered (single-context user).
   */
  readonly showTitleBlock = computed(
    () => !this.isFullPage() || this.chatHeaderContexts().length === 0,
  );

  @HostBinding('class.full-page')
  get fullPageClass() {
    return this.isFullPage();
  }

  internalMessageStatus = InternalMessageStatus;

  readonly feedbackCommentMaxLength = FEEDBACK_COMMENT_MAX_LENGTH;

  chatQuestionForm = this.#formBuilder.group({
    question: new FormControl(''),
  });

  feedbackForm = this.#formBuilder.group<ModelFormGroup<ChatMessageFeedback>>({
    feedbackType: new FormControl(null, [Validators.required]),
    text: new FormControl('', [Validators.maxLength(FEEDBACK_COMMENT_MAX_LENGTH)]),
  });

  dislikeReasons = this.#dictionariesService.dislikeReasons;
  isLoading = this.#aiChatStore.isLoading;
  feedbackMessageId = this.#aiChatStore.feedbackMessageId;
  disableSendQuestion = this.#aiChatStore.disableSendQuestion;
  historySidebarCollapsed = this.#aiChatStore.historySidebarCollapsed;

  readonly messages = this.#aiChatStore.messages;
  readonly chats = this.#aiChatStore.chats;
  readonly chatsLoading = this.#aiChatStore.chatsLoading;
  readonly error = this.#aiChatStore.error;
  readonly authSessionError = computed(
    () => {
      const error = this.error();

      return error && isAuthSessionError(error) ? error : null;
    },
  );
  readonly systemError = computed(
    () => {
      const error = this.error();

      return error && !isAuthSessionError(error) ? error : null;
    },
  );
  readonly chatLoading = this.#aiChatStore.chatLoading;
  readonly isWorkContextPickerOpen = this.#aiChatStore.isWorkContextPickerOpen;
  /** Expose role enum to template. */
  readonly ChatMessageRole = ChatMessageRole;

  /** CTAs for the active surface: page-context in the popup, fixed set in full-page. */
  readonly chatCtas = this.#ctaStore.visibleCtas;

  /**
   * Full-page always shows its fixed set; the popup shows them only on a supported
   * page with the history overlay closed. Both hide while the work context picker is
   * open, there are no CTAs, or the active context is Work.
   */
  readonly showChatCtas = computed(() => {
    if (
      this.isWorkContextPickerOpen()
      || this.chatCtas().length === 0
      || this.tabNav?.activeContext() === AiChatContext.Work
    ) {
      return false;
    }

    return this.isFullPage()
      ? true
      : this.#ctaStore.isSupportedContext() && !this.popupHistoryOpen();
  });

  /**
   * Translation key for the welcome greeting shown when no messages are loaded.
   * Depends on the active context and the For Work role from the tab bridge.
   */
  readonly welcomeGreetingKey = computed((): string => {
    if (this.tabNav?.activeContext() !== AiChatContext.Work) {
      return 'chatBot.greeting';
    }
    const role = this.tabNav.forWorkRole();

    return role === 'support' ? 'chatBot.greeting.workSupport' : 'chatBot.greeting.workHr';
  });

  /** Title of the currently selected chat; null when in a new/draft session. */
  readonly popupChatTitle = computed((): string | null => {
    const id = this.#aiChatStore.selectedChatId();
    if (!id) return null;
    const chat = this.#aiChatStore.chats().find(chatItem => chatItem.id === id);
    if (!chat) return null;

    return formatAiChatThreadListTitle(chat.title, this.#datePipe)
      || this.#datePipe.transform(chat.updatedAt, 'dd.MM.yyyy HH:mm');
  });

  /**
   * Badge of the selected chat (server label, else license, else work scope); null for new chats
   * or chats with none.
   */
  readonly selectedChatBadge = computed((): string | null => {
    const id = this.#aiChatStore.selectedChatId();
    if (!id) return null;

    const chat = this.#aiChatStore.chats().find(item => item.id === id);

    return chat ? workChatBadge(chat) : null;
  });

  /** True when the tabs/context initialization API call failed. */
  readonly tabsInitError = computed(() => !!this.tabNav?.tabsInitError());
  readonly tabsAuthSessionError = computed(() => {
    const error = this.tabNav?.tabsInitError();

    return error && isAuthSessionError(error) ? error : null;
  });
  readonly tabsSystemError = computed(() => {
    const error = this.tabNav?.tabsInitError();

    return error && !isAuthSessionError(error) ? error : null;
  });

  /** Stable timestamp for the empty-state greeting so its bubble shows a fixed time. */
  readonly #welcomeCreatedAt = new Date().toISOString();

  /**
   * Render the empty-state greeting through the same bubble as any bot reply
   * (avatar + name + time), so the welcome view matches the static "Any
   * questions left?" reply and does not visually jump when a message is added.
   */
  welcomeMessageView(text: string): BotChatMessageView {
    return {
      id: 'welcome',
      chatId: '',
      request: '',
      reply: text,
      replyType: ChatMessageReplyTypes.SIMPLE,
      isSuccessReply: true,
      createdAt: this.#welcomeCreatedAt,
      lang: AI_CHAT_DEFAULT_GREETING_LANG as 'ru' | 'en' | 'uk',
      links: null as null,
      timeOffBalances: [] as never[],
      internalStatus: InternalMessageStatus.resolved,
      useBlockMarkdown: true,
    };
  }

  /** Map an AGENT message to the minimal shape expected by BotChatMessageComponent. */
  agentMsgToDisplay(msg: ChatMessage): BotChatMessageView {
    const isPendingOrTimeout =
      !msg.content
      && (msg.internalStatus === InternalMessageStatus.pending
        || msg.internalStatus === InternalMessageStatus.timeout);

    return {
      id: msg.id,
      chatId: msg.chatId,
      request: '',
      reply: msg.content,
      replyType: ChatMessageReplyTypes.SIMPLE,
      isSuccessReply: !isPendingOrTimeout,
      createdAt: msg.createdAt,
      lang: msg.language.toLowerCase() as 'ru' | 'en' | 'uk',
      links: (msg.sources ?? []).map(source => ({ label: source.pageTitle, url: source.pageUrl })),
      timeOffBalances: [] as never[],
      internalStatus: msg.internalStatus ?? InternalMessageStatus.resolved,
      useBlockMarkdown: true,
      reactionType: msg.reactionType,
      isFeedbackProvided: msg.isFeedbackProvided,
    };
  }

  /** Full-page: true while pinned to the newest messages; false once the user scrolls up. */
  readonly #isPinnedToBottom = signal(true);

  /** Id of the currently opened chat; changes only on chat/thread switch. */
  readonly #openedChatKey = computed((): string | null => this.#aiChatStore.selectedChatId());

  constructor() {
    this.#aiChatStore.startNewChat();
    this.#aiChatStore.loadChats();
    this.#initChatCtas();
    this.#applyInitialAction();

    /**
     * Popup mode: `ai-chat-page` is NOT in the injector tree, so it never calls
     * `loadTabsAndLicenses()`. Bootstrap it here when running as a dialog.
     * Full-page mode: `ai-chat-page` already handles this — skip to avoid a duplicate call.
     */
    if (this.tabNav) {
      afterNextRender(
        () => {
          if (!this.isFullPage()) {
            this.tabNav!
              .loadTabsAndLicenses({ disableNavigation: true })
              .pipe(takeUntilDestroyed(this.#destroyRef))
              .subscribe();
          }
        },
        { injector: this.#injector },
      );
    }

    effect(() => {
      this.#openedChatKey();
      const body = this.#fullPageBody();

      if (!body) {
        return;
      }

      untracked(() => {
        this.#isPinnedToBottom.set(true);
        queueMicrotask(() => this.#scrollFullPageToNewest(body));
      });
    }, { allowSignalWrites: true });

    effect(() => {
      this.messages();
      this.isLoading();
      const body = this.#fullPageBody();

      if (!body) {
        return;
      }

      untracked(() => {
        if (this.#isPinnedToBottom()) {
          queueMicrotask(() => this.#scrollFullPageToNewest(body));
        }
      });
    });
  }

  /** Update the pinned-to-bottom flag; in `column-reverse` the bottom is `scrollTop` 0. */
  onMessagesScroll(): void {
    const body = this.#fullPageBody();

    if (!body) {
      return;
    }

    this.#isPinnedToBottom.set(Math.abs(body.scrollTop) <= AI_CHAT_SCROLL_BOTTOM_THRESHOLD_PX);
  }

  leaveFeedback() {
    if (this.feedbackForm.invalid) {
      return;
    }

    const { feedbackType, text } = this.feedbackForm.getRawValue();

    this.#aiChatStore.leaveFeedback({
      id: this.feedbackMessageId(),
      reason: feedbackType,
      comment: text ?? '',
    });

    this.feedbackForm.reset();
    this.#aiChatStore.closeFeedbackForm();
  }

  closeFeedbackForm() {
    this.#aiChatStore.closeFeedbackForm();
  }

  send() {
    const request = this.chatQuestionForm.controls.question.value;

    if (this.chatQuestionForm.invalid || !request) {
      return;
    }

    // Cleared first: a dismissed work context picker may hand the text back synchronously.
    this.chatQuestionForm.controls.question.patchValue('');
    this.#aiChatStore.sendMessage(request, undefined, text => this.#restoreQuestion(text));
    queueMicrotask(() => this.questionAutosize()?.resize());
  }

  /** Puts back a question the work context picker returned unsent, unless a new one was typed. */
  #restoreQuestion(text: string): void {
    const question = this.chatQuestionForm.controls.question;

    if (question.value) {
      return;
    }

    question.patchValue(text);
    queueMicrotask(() => this.questionAutosize()?.resize());
  }

  openPopupHistory(): void {
    this.#popupHistoryOpen.set(true);
  }

  closePopupHistory(): void {
    this.#popupHistoryOpen.set(false);
  }

  /** Enter sends; Shift+Enter newline. */
  onQuestionKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== 'NumpadEnter') {
      return;
    }
    if (event.shiftKey) {
      return;
    }
    event.preventDefault();
    if (!this.disableSendQuestion()) {
      this.send();
    }
  }

  close() {
    this.#dialogRef?.close();
  }

  expandHistorySidebar(): void {
    this.#aiChatStore.toggleHistorySidebarCollapsed();
  }

  chatContextLabelKey(context: AiChatContext): string {
    return context === AiChatContext.Work
      ? 'chatBot.tabForWork'
      : 'chatBot.tabForMe';
  }

  navigateToChatContext(context: AiChatContext): void {
    if (!this.tabNav) return;

    if (this.isFullPage()) {
      this.tabNav.requestNavigateToContext(context);
    } else {
      this.tabNav.switchContextInPlace(context);
    }
  }

  /** Full-page messages container, or null in popup mode / before it renders. */
  #fullPageBody(): HTMLElement | null {
    return this.isFullPage() ? this.messagesBody()?.nativeElement ?? null : null;
  }

  #scrollFullPageToNewest(body: HTMLElement): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        body.scrollTop = 0;
      });
    });
  }

  /**
   * Triggered when an in-chat CTA (popup or full-page) is clicked: start the
   * cooldown optimistically (so it hides at once and stays hidden on every CTA
   * surface), then run the prompt or static reply. A failed bot request rolls
   * the cooldown back via the action's onError.
   */
  protected onChatCtaSelected(cta: CtaDefinition): void {
    this.#ctaCooldown.start(cta);
    this.#executeInitialAction(ctaToInitialAction(cta, () => this.#ctaCooldown.cancel(cta.id)));
  }

  /**
   * Bind the CTA surface to the view and load cooldowns. `isFullPage` is an input,
   * so it resolves after construction — track it to switch surface once it settles.
   */
  #initChatCtas(): void {
    this.#ctaStore.syncCooldowns();

    effect(
      () => {
        const surface = this.isFullPage() ? CtaSurface.FullPage : CtaSurface.Popup;
        untracked(() => {
          this.#ctaStore.setSurface(surface);
          if (surface === CtaSurface.Popup) {
            this.#ctaStore.setCurrentPath(this.#pageContext.currentPath());
          }
        });
      },
      { allowSignalWrites: true },
    );
  }

  /** Apply a CTA-driven launch action: static canned reply or a bot prompt. */
  #applyInitialAction(): void {
    const action = this.#dialogData?.initialAction;

    if (action) {
      this.#executeInitialAction(action);
    }
  }

  #executeInitialAction(action: AiChatInitialAction): void {
    if (action.staticReplyKey) {
      const reply = this.#dictionariesService.translate(
        action.staticReplyKey,
        AI_CHAT_DEFAULT_GREETING_LANG,
      );
      this.#aiChatStore.pushStaticReply(reply);

      return;
    }

    if (action.prompt) {
      this.#aiChatStore.sendMessage(action.prompt, action.onError);
    }
  }
}
